/**
 * Calculadora de Costos de Confección
 * Integración con Backend: sizeSpec, garmentType, costCalculator
 * UX: Cascading dropdowns (Tipo → Talla → Variante), datos tela, resultados con desglose
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Button,
  Form,
  Spinner,
  Row,
  Col,
  Table,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import { getSizeSpecs, getGarmentTypes, calculateCost } from '../services/costCalculatorApi';
import { sortBySizeCode } from '../utils/sizeOrder';
import './CostCalculator.css';

const LABOR_COST_PER_MINUTE = 220;

const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.message)) return data.message;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

/** Obtiene size_code únicos ordenados */
const getUniqueSizeCodes = (sizeSpecs) => {
  const codes = [...new Set(sizeSpecs.map((s) => s.size_code).filter(Boolean))];
  return sortBySizeCode(codes.map((c) => ({ size_code: c }))).map((s) => s.size_code);
};

const CostCalculator = () => {
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [garmentTypes, setGarmentTypes] = useState([]);
  const [sizeSpecs, setSizeSpecs] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [loadingSizes, setLoadingSizes] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [sobranteSuggestion, setSobranteSuggestion] = useState(null);
  const [sobranteLoading, setSobranteLoading] = useState(false);

  const [form, setForm] = useState({
    garment_type_id: '',
    size_code: '',
    size_spec_id: '',
    fabric_width: 150,
    fabric_price: '',
    cutting_time_min: '',
    sewing_time_min: '',
    finishing_time_min: '',
    supplies_cost: '',
  });

  const loadGarmentTypes = useCallback(async () => {
    setLoadingTypes(true);
    const { ok, data } = await getGarmentTypes();
    setLoadingTypes(false);
    if (ok) {
      setGarmentTypes(extractArray(data));
    } else {
      showAlert(getErrorMessage({ data }) || 'Error cargando tipos de prenda', 'error');
    }
  }, [showAlert, getErrorMessage]);

  const loadSizeSpecs = useCallback(async () => {
    setLoadingSizes(true);
    const { ok, data } = await getSizeSpecs();
    setLoadingSizes(false);
    if (ok) {
      setSizeSpecs(sortBySizeCode(extractArray(data)));
    } else {
      showAlert(getErrorMessage({ data }) || 'Error cargando tallas', 'error');
    }
  }, [showAlert, getErrorMessage]);

  useEffect(() => {
    loadGarmentTypes();
    loadSizeSpecs();
  }, [loadGarmentTypes, loadSizeSpecs]);

  const sizeCodes = getUniqueSizeCodes(sizeSpecs);
  const variantsForSize = form.size_code
    ? sizeSpecs.filter((s) => String(s.size_code) === String(form.size_code))
    : [];

  const handleGarmentTypeChange = (id) => {
    const type = garmentTypes.find((t) => (t.id ?? t.id_garment_type) == id);
    setForm((p) => ({
      ...p,
      garment_type_id: id,
      supplies_cost: type?.default_supplies_cost ?? type?.supplies_cost ?? '',
    }));
  };

  const totalLaborMinutes = () => {
    const cut = parseFloat(form.cutting_time_min) || 0;
    const sew = parseFloat(form.sewing_time_min) || 0;
    const fin = parseFloat(form.finishing_time_min) || 0;
    return cut + sew + fin;
  };

  const calculatedLaborCost = totalLaborMinutes() * LABOR_COST_PER_MINUTE;

  const handleSizeCodeChange = (code) => {
    setForm((p) => ({
      ...p,
      size_code: code,
      size_spec_id: '',
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.garment_type_id || !form.size_spec_id) {
      showAlert('Selecciona tipo de prenda, talla y variante', 'error');
      return;
    }
    const fabricWidth = parseFloat(form.fabric_width);
    const fabricPrice = parseFloat(form.fabric_price);
    if (isNaN(fabricWidth) || fabricWidth <= 0) {
      showAlert('El ancho de la tela es obligatorio y debe ser mayor a 0', 'error');
      return;
    }
    if (isNaN(fabricPrice) || fabricPrice < 0) {
      showAlert('Ingresa el precio de la tela', 'error');
      return;
    }
    setCalculating(true);
    setResult(null);
    setSobranteSuggestion(null);
    try {
      const payload = {
        garment_type_id: parseInt(form.garment_type_id, 10),
        size_spec_id: parseInt(form.size_spec_id, 10),
        fabric_width: fabricWidth,
        fabric_price: fabricPrice,
        labor_cost: calculatedLaborCost,
      };
      if (form.supplies_cost !== '' && form.supplies_cost != null) {
        const supplies = parseFloat(form.supplies_cost);
        if (!isNaN(supplies)) payload.supplies_cost = supplies;
      }
      const { ok, status, data } = await calculateCost(payload);
      if (ok && data && !data.error) {
        const resultData = data.message && typeof data.message === 'object' && (data.message.unit_cost != null || data.message.fabric_cost != null)
          ? data.message
          : data;
        setResult(resultData);
      } else {
        const msg = data?.message || data?.error || getErrorMessage({ data }) || 'Error al calcular';
        const is400 = status === 400;
        const fullMsg = is400
          ? `${msg} Sugerencia: Usar una tela más ancha o rotar el molde.`
          : msg;
        showAlert(fullMsg, 'error');
      }
    } catch (err) {
      showAlert('Error inesperado. Verifica la conexión.', 'error');
    } finally {
      setCalculating(false);
    }
  };

  const unitCost = result?.unit_cost ?? result?.cost ?? 0;
  const suggestedPrice = result?.suggested_price ?? result?.price ?? unitCost * 2.2;
  const itemsPerMeter = result?.items_per_meter ?? result?.rendimiento;
  const breakdown = result?.breakdown ?? {};
  const fabricCost = breakdown.fabric_cost ?? result?.fabric_cost;
  const laborCostBreakdown = breakdown.labor_cost ?? result?.labor_cost;
  const suppliesCostBreakdown = breakdown.supplies_cost ?? result?.supplies_cost;

  const fullGarmentsPerMeter = itemsPerMeter != null ? Math.floor(itemsPerMeter) : 0;
  const sobranteMeters = useMemo(() => {
    if (itemsPerMeter == null || itemsPerMeter <= 0) return 0;
    const decimal = itemsPerMeter - Math.floor(itemsPerMeter);
    if (decimal < 0.001) return 0;
    return decimal / itemsPerMeter;
  }, [itemsPerMeter]);

  useEffect(() => {
    if (!result || sobranteMeters < 0.001 || !form.size_spec_id || !form.garment_type_id) {
      setSobranteSuggestion(null);
      return;
    }
    const otherSizes = sizeSpecs.filter((s) => String(s.id) !== String(form.size_spec_id));
    if (otherSizes.length === 0) {
      setSobranteSuggestion(null);
      return;
    }
    const laborCost = (parseFloat(form.cutting_time_min) || 0) + (parseFloat(form.sewing_time_min) || 0) + (parseFloat(form.finishing_time_min) || 0);
    const basePayload = {
      garment_type_id: parseInt(form.garment_type_id, 10),
      fabric_width: parseFloat(form.fabric_width) || 150,
      fabric_price: parseFloat(form.fabric_price) || 0,
      labor_cost: laborCost * LABOR_COST_PER_MINUTE,
    };
    if (form.supplies_cost !== '' && form.supplies_cost != null) {
      const supplies = parseFloat(form.supplies_cost);
      if (!isNaN(supplies)) basePayload.supplies_cost = supplies;
    }
    setSobranteLoading(true);
    Promise.all(
      otherSizes.map(async (sizeSpec) => {
        const { ok, data } = await calculateCost({ ...basePayload, size_spec_id: sizeSpec.id });
        const res = data?.message && typeof data.message === 'object' ? data.message : data;
        const ipm = res?.items_per_meter ?? res?.rendimiento;
        return { sizeSpec, itemsPerMeter: ipm };
      })
    )
      .then((results) => {
        const valid = results
          .filter((r) => r.itemsPerMeter != null && r.itemsPerMeter > 0)
          .map((r) => ({
            ...r,
            garmentsThatFit: sobranteMeters * r.itemsPerMeter,
          }))
          .filter((r) => r.garmentsThatFit >= 1);
        if (valid.length === 0) {
          setSobranteSuggestion(null);
          return;
        }
        const best = valid.sort((a, b) => a.itemsPerMeter - b.itemsPerMeter)[0];
        setSobranteSuggestion({
          sizeLabel: [best.sizeSpec.size_code, best.sizeSpec.size_variant || best.sizeSpec.variant].filter(Boolean).join(' - ') || `Talla ${best.sizeSpec.id}`,
          count: Math.floor(best.garmentsThatFit),
        });
      })
      .catch(() => setSobranteSuggestion(null))
      .finally(() => setSobranteLoading(false));
  }, [result, sobranteMeters, form.size_spec_id, form.garment_type_id, form.fabric_width, form.fabric_price, form.supplies_cost, form.cutting_time_min, form.sewing_time_min, form.finishing_time_min, sizeSpecs]);

  return (
    <>
      <Helmet>
        <title>Calculadora de Costos de Confección - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="cost-calc-container">
        <div className="cost-calc-header">
          <h1 className="cost-calc-title">Calculadora de Costos de Confección</h1>
          <p className="cost-calc-subtitle">
            Calcula el costo unitario y precio sugerido para prendas de mascotas.
          </p>
        </div>

        <div className="cost-calc-layout">
          <Card className="cost-calc-form-card">
            <Card.Body>
              <Form onSubmit={handleSubmit}>
                <div className="cost-calc-section">
                  <h6 className="cost-calc-section-title">Datos de la prenda</h6>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de prenda *</Form.Label>
                    <Form.Select
                      value={form.garment_type_id}
                      onChange={(e) => handleGarmentTypeChange(e.target.value)}
                      required
                      className="cost-calc-input"
                    >
                      <option value="">Seleccionar...</option>
                      {garmentTypes.map((t) => (
                        <option key={t.id ?? t.id_garment_type} value={t.id ?? t.id_garment_type}>
                          {t.name || t.garment_type_name || `Tipo ${t.id}`}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Talla *</Form.Label>
                    <Form.Select
                      value={form.size_code}
                      onChange={(e) => handleSizeCodeChange(e.target.value)}
                      required
                      className="cost-calc-input"
                    >
                      <option value="">Seleccionar...</option>
                      {sizeCodes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Variante / Tamaño *</Form.Label>
                    <Form.Select
                      value={form.size_spec_id}
                      onChange={(e) => setForm((p) => ({ ...p, size_spec_id: e.target.value }))}
                      required
                      disabled={!form.size_code}
                      className="cost-calc-input"
                    >
                      <option value="">
                        {form.size_code ? 'Seleccionar variante...' : 'Primero selecciona una talla'}
                      </option>
                      {variantsForSize.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.size_variant || s.variant || 'Estándar'}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="cost-calc-section">
                  <h6 className="cost-calc-section-title">Datos de la tela</h6>
                  <Row className="g-2">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ancho tela (cm) *</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={form.fabric_width}
                          onChange={(e) => setForm((p) => ({ ...p, fabric_width: e.target.value }))}
                          placeholder="150"
                          required
                          className="cost-calc-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Precio tela por metro ($) *</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={form.fabric_price}
                          onChange={(e) => setForm((p) => ({ ...p, fabric_price: e.target.value }))}
                          placeholder="0"
                          required
                          className="cost-calc-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>

                <div className="cost-calc-section">
                  <h6 className="cost-calc-section-title">Tiempos de fabricación (minutos)</h6>
                  <p className="cost-calc-section-hint">Mano de obra = suma de tiempos × $220/min</p>
                  <Row className="g-2">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tiempo de corte</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.5"
                          min="0"
                          value={form.cutting_time_min}
                          onChange={(e) => setForm((p) => ({ ...p, cutting_time_min: e.target.value }))}
                          placeholder="0"
                          className="cost-calc-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tiempo de confección</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.5"
                          min="0"
                          value={form.sewing_time_min}
                          onChange={(e) => setForm((p) => ({ ...p, sewing_time_min: e.target.value }))}
                          placeholder="0"
                          className="cost-calc-input"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Tiempo de terminación</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.5"
                          min="0"
                          value={form.finishing_time_min}
                          onChange={(e) => setForm((p) => ({ ...p, finishing_time_min: e.target.value }))}
                          placeholder="0"
                          className="cost-calc-input"
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  {totalLaborMinutes() > 0 && (
                    <div className="cost-calc-labor-preview">
                      Costo mano de obra: $ {calculatedLaborCost.toLocaleString('es-CO')} ({totalLaborMinutes()} min × $220/min)
                    </div>
                  )}
                </div>

                <div className="cost-calc-section">
                  <h6 className="cost-calc-section-title">Costos opcionales</h6>
                  <Form.Group className="mb-3">
                    <Form.Label>Insumos ($)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.supplies_cost}
                      onChange={(e) => setForm((p) => ({ ...p, supplies_cost: e.target.value }))}
                      placeholder="Opcional"
                      className="cost-calc-input"
                    />
                  </Form.Group>
                </div>

                <div className="cost-calc-actions">
                  <Button
                    type="submit"
                    className="cost-calc-btn-calc"
                    disabled={loadingTypes || loadingSizes || calculating}
                  >
                    {calculating ? <Spinner animation="border" size="sm" /> : 'Calcular Costo'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          <Card className="cost-calc-results-card">
            <Card.Body>
              <h5 className="cost-calc-results-title">Resultados</h5>
              {result ? (
                <>
                  <div className="cost-calc-unit-cost">
                    <span className="cost-calc-unit-cost-label">Costo Unitario Total</span>
                    <span className="cost-calc-unit-cost-value">
                      $ {Number(unitCost).toLocaleString('es-CO')}
                    </span>
                  </div>
                  <div className="cost-calc-suggested-price">
                    <span className="cost-calc-suggested-label">Precio Sugerido</span>
                    <span className="cost-calc-suggested-value">
                      $ {Number(suggestedPrice).toLocaleString('es-CO')}
                    </span>
                  </div>
                  {itemsPerMeter != null && (
                    <>
                      <div className="cost-calc-rendimiento">
                        Te salen <strong>{fullGarmentsPerMeter}</strong> prenda{fullGarmentsPerMeter !== 1 ? 's' : ''} por metro
                        {sobranteMeters > 0 && (
                          <span className="cost-calc-rendimiento-decimal">
                            {' '}(sobrante: ~{(sobranteMeters * 100).toFixed(0)} cm)
                          </span>
                        )}
                      </div>
                      {sobranteMeters > 0 && (
                        <div className="cost-calc-sobrante">
                          <h6 className="cost-calc-sobrante-title">Sobrante del metro</h6>
                          {sobranteLoading ? (
                            <div className="cost-calc-sobrante-loading">
                              <Spinner animation="border" size="sm" /> Calculando...
                            </div>
                          ) : sobranteSuggestion ? (
                            <p className="cost-calc-sobrante-text">
                              Con el sobrante te alcanza para{' '}
                              <strong>{sobranteSuggestion.count} prenda{sobranteSuggestion.count !== 1 ? 's' : ''}</strong>{' '}
                              de la talla <strong>{sobranteSuggestion.sizeLabel}</strong>
                            </p>
                          ) : (
                            <p className="cost-calc-sobrante-text cost-calc-sobrante-no-fit">
                              El sobrante no alcanza para otra prenda completa
                            </p>
                          )}
                        </div>
                      )}
                    </>
                  )}
                  {(fabricCost != null || laborCostBreakdown != null || suppliesCostBreakdown != null) && (
                    <div className="cost-calc-breakdown">
                      <h6 className="cost-calc-breakdown-title">Desglose</h6>
                      <Table size="sm" className="cost-calc-breakdown-table">
                        <tbody>
                          {fabricCost != null && (
                            <tr>
                              <td>Tela</td>
                              <td className="text-end">$ {Number(fabricCost).toLocaleString('es-CO')}</td>
                            </tr>
                          )}
                          {laborCostBreakdown != null && (
                            <tr>
                              <td>Mano de obra</td>
                              <td className="text-end">$ {Number(laborCostBreakdown).toLocaleString('es-CO')}</td>
                            </tr>
                          )}
                          {suppliesCostBreakdown != null && (
                            <tr>
                              <td>Insumos</td>
                              <td className="text-end">$ {Number(suppliesCostBreakdown).toLocaleString('es-CO')}</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              ) : (
                <div className="cost-calc-empty-results">
                  <p>Completa el formulario y haz clic en <strong>Calcular Costo</strong> para ver los resultados.</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
    </>
  );
};

export default CostCalculator;
